/* ============================================================
   VINNESSES — theme.js
   No dependencies. Custom elements + IntersectionObserver.
   Every interaction degrades to working HTML without JS.
   ============================================================ */
(function () {
  'use strict';

  document.documentElement.classList.add('js');

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  var FOCUSABLE = [
    'a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  /* ---------- helpers -------------------------------------- */
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function t(key, fallback) {
    var s = window.Vinnesses && window.Vinnesses.strings;
    return (s && s[key]) || fallback;
  }

  function fetchJSON(url, options) {
    return fetch(url, options).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) throw data;
        return data;
      });
    });
  }

  /* ---------- Toast ----------------------------------------
     Announced politely; never steals focus.                   */
  var Toast = (function () {
    var region;
    function ensure() {
      if (region) return region;
      region = $('.toast-region');
      if (!region) {
        region = document.createElement('div');
        region.className = 'toast-region';
        region.setAttribute('role', 'status');
        region.setAttribute('aria-live', 'polite');
        region.setAttribute('aria-atomic', 'false');
        document.body.appendChild(region);
      }
      return region;
    }
    function show(message, opts) {
      opts = opts || {};
      var host = ensure();
      var el = document.createElement('div');
      el.className = 'toast' + (opts.error ? ' toast--error' : '');
      var icon = opts.error
        ? '<svg viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="1.3"/><path d="M10 6v5M10 13.6v.4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>'
        : '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4.5 10.5l3.5 3.5 7.5-8" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      var body = '<div class="toast__body">' + message;
      if (opts.link) body += '<a class="toast__link" href="' + opts.link.href + '">' + opts.link.text + '</a>';
      body += '</div>';
      el.innerHTML = icon + body;
      host.appendChild(el);
      requestAnimationFrame(function () { el.classList.add('is-visible'); });

      var life = opts.duration || 4200;
      var timer = setTimeout(dismiss, life);
      el.addEventListener('mouseenter', function () { clearTimeout(timer); });
      el.addEventListener('mouseleave', function () { timer = setTimeout(dismiss, 1600); });

      function dismiss() {
        clearTimeout(timer);
        el.classList.add('is-leaving');
        el.addEventListener('transitionend', function () { el.remove(); }, { once: true });
        // Never depend on transitionend alone for correctness.
        setTimeout(function () { if (el.isConnected) el.remove(); }, 600);
      }
      return dismiss;
    }
    return { show: show };
  })();
  window.VinToast = Toast;

  /* ---------- Focus trap ----------------------------------- */
  function trapFocus(container, opener) {
    var previous = opener || document.activeElement;

    function onKey(e) {
      if (e.key !== 'Tab') return;
      var items = $$(FOCUSABLE, container).filter(function (el) {
        return el.offsetParent !== null || el === document.activeElement;
      });
      if (!items.length) return;
      var first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    container.addEventListener('keydown', onKey);
    return function release(restore) {
      container.removeEventListener('keydown', onKey);
      if (restore !== false && previous && typeof previous.focus === 'function') {
        previous.focus({ preventScroll: true });
      }
    };
  }

  /* ---------- Scroll lock (preserves position) ------------- */
  var lockCount = 0, savedScroll = 0;
  function lockScroll() {
    if (lockCount++ > 0) return;
    savedScroll = window.scrollY;
    document.body.style.top = -savedScroll + 'px';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.classList.add('is-locked');
  }
  function unlockScroll() {
    if (--lockCount > 0) return;
    lockCount = 0;
    document.body.classList.remove('is-locked');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, savedScroll);
  }

  /* ==========================================================
     <vin-drawer> — cart drawer, mobile nav, search
     ========================================================== */
  class VinDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.drawer');
      this.overlay = this.querySelector('.overlay');
      this.release = null;
      this.isOpen = false;

      this.onKey = function (e) { if (e.key === 'Escape') this.close(); }.bind(this);

      if (this.overlay) this.overlay.addEventListener('click', this.close.bind(this));

      // Delegated, so close controls inside content replaced by the Section
      // Rendering API keep working without re-binding.
      this.addEventListener('click', function (e) {
        if (e.target.closest('[data-drawer-close]')) {
          e.preventDefault();
          this.close();
        }
      }.bind(this));

      // Any control anywhere can open this drawer by id.
      var id = this.id;
      $$('[data-drawer-open="' + id + '"]').forEach(function (btn) {
        btn.setAttribute('aria-haspopup', 'dialog');
        btn.setAttribute('aria-expanded', 'false');
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          this.open(btn);
        }.bind(this));
      }, this);
    }

    open(opener) {
      if (this.isOpen) return;
      this.isOpen = true;
      lockScroll();
      if (this.overlay) this.overlay.classList.add('is-open');
      this.panel.classList.add('is-open');
      this.panel.setAttribute('aria-hidden', 'false');
      document.addEventListener('keydown', this.onKey);
      this.release = trapFocus(this.panel, opener);
      $$('[data-drawer-open="' + this.id + '"]').forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });

      // Move focus in after the panel is on screen, not before.
      var target = this.panel.querySelector('[data-drawer-initial-focus]') || this.panel.querySelector('[data-drawer-close]');
      if (target) setTimeout(function () { target.focus({ preventScroll: true }); }, reduced.matches ? 0 : 180);
      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      if (this.overlay) this.overlay.classList.remove('is-open');
      this.panel.classList.remove('is-open');
      this.panel.setAttribute('aria-hidden', 'true');
      document.removeEventListener('keydown', this.onKey);
      unlockScroll();
      $$('[data-drawer-open="' + this.id + '"]').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      if (this.release) { this.release(true); this.release = null; }
      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }
  }
  customElements.define('vin-drawer', VinDrawer);

  /* ==========================================================
     Cart — add, change, re-render via Section Rendering API
     ========================================================== */
  var Cart = {
    drawer: function () { return document.getElementById('cart-drawer'); },

    /* The count and its accessible label are re-rendered server-side, so
       this only plays the acknowledgement. */
    bump: function () {
      if (reduced.matches) return;
      $$('[data-cart-count]').forEach(function (el) {
        el.classList.remove('is-bumped');
        // Force reflow so a rapid second add re-runs the bump from the start.
        void el.offsetWidth;
        el.classList.add('is-bumped');
        setTimeout(function () { el.classList.remove('is-bumped'); }, 220);
      });
    },

    renderSections: function (sections) {
      if (!sections) return;
      Object.keys(sections).forEach(function (key) {
        var html = sections[key];
        if (!html) return;
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        $$('[data-cart-section="' + key + '"]').forEach(function (target) {
          var fresh = parsed.querySelector('[data-cart-section="' + key + '"]');
          if (fresh) target.innerHTML = fresh.innerHTML;
        });
      });
    },

    sectionIds: function () {
      return $$('[data-cart-section]')
        .map(function (el) { return el.getAttribute('data-cart-section'); })
        .filter(function (v, i, a) { return v && a.indexOf(v) === i; });
    },

    add: function (formData) {
      var ids = this.sectionIds();
      if (ids.length) formData.append('sections', ids.join(','));
      return fetchJSON(window.Routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { Accept: 'application/javascript' },
        body: formData
      }).then(function (data) {
        Cart.renderSections(data.sections);
        Cart.bump();
        document.dispatchEvent(new CustomEvent('cart:updated'));
        return data;
      });
    },

    change: function (payload) {
      var ids = this.sectionIds();
      return fetchJSON(window.Routes.cart_change_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(Object.assign({ sections: ids.join(',') }, payload))
      }).then(function (cart) {
        Cart.renderSections(cart.sections);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
        return cart;
      });
    }
  };
  window.VinCart = Cart;

  /* ---------- <vin-add-to-cart> ---------------------------- */
  class VinAddToCart extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.button = this.querySelector('[data-atc]');
      this.errorEl = this.querySelector('[data-atc-error]');
      if (!this.form) return;
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    onSubmit(e) {
      if (!window.fetch || !window.Routes) return;   // let the form post normally
      e.preventDefault();
      if (this.button && this.button.classList.contains('is-loading')) return;

      this.setError('');
      if (this.button) {
        this.button.classList.add('is-loading');
        this.button.setAttribute('aria-disabled', 'true');
      }

      var data = new FormData(this.form);
      Cart.add(data)
        .then(function () {
          var drawer = Cart.drawer();
          if (drawer) drawer.open(this.button);
          else Toast.show(t('addedToCart', 'Added to your cart.'), {
            link: { href: window.Routes.cart_url, text: t('viewCart', 'View cart') }
          });
        }.bind(this))
        .catch(function (err) {
          var msg = (err && (err.description || err.message)) || t('addError', 'This item could not be added.');
          this.setError(msg);
          Toast.show(msg, { error: true });
        }.bind(this))
        .then(function () {
          if (this.button) {
            this.button.classList.remove('is-loading');
            this.button.removeAttribute('aria-disabled');
          }
        }.bind(this));
    }

    setError(msg) {
      if (!this.errorEl) return;
      this.errorEl.textContent = msg;
      this.errorEl.hidden = !msg;
    }
  }
  customElements.define('vin-add-to-cart', VinAddToCart);

  /* ---------- <vin-cart-line> ------------------------------ */
  class VinCartLine extends HTMLElement {
    connectedCallback() {
      this.key = this.dataset.key;
      this.input = this.querySelector('[data-qty-input]');

      this.addEventListener('click', function (e) {
        var remove = e.target.closest('[data-line-remove]');
        if (remove) { e.preventDefault(); this.update(0); return; }
        var step = e.target.closest('[data-qty-step]');
        if (step && this.input) {
          e.preventDefault();
          var next = parseInt(this.input.value, 10) + parseInt(step.dataset.qtyStep, 10);
          if (next < 0) next = 0;
          this.input.value = next;
          this.update(next);
        }
      }.bind(this));

      if (this.input) {
        this.input.addEventListener('change', function () {
          var v = parseInt(this.input.value, 10);
          this.update(isNaN(v) || v < 0 ? 0 : v);
        }.bind(this));
      }
    }

    update(quantity) {
      if (!window.fetch || !window.Routes) return;
      this.classList.add('is-updating');
      var title = (this.dataset.title || t('item', 'Item'));
      Cart.change({ id: this.key, quantity: quantity })
        .then(function () {
          if (quantity === 0) {
            Toast.show(t('removed', '{{ title }} removed from your cart.').replace('{{ title }}', title));
          }
        })
        .catch(function () {
          Toast.show(t('cartError', 'Your cart could not be updated.'), { error: true });
          this.classList.remove('is-updating');
        }.bind(this));
    }
  }
  customElements.define('vin-cart-line', VinCartLine);

  /* ==========================================================
     <vin-variants> — variant selection
     Writes to a hidden input and the URL; no data invented.
     ========================================================== */
  class VinVariants extends HTMLElement {
    connectedCallback() {
      // The picker and the buy buttons are sibling section blocks, not nested,
      // so reach the form by id rather than walking up the tree.
      this.form = document.getElementById('product-form')
        || document.querySelector('vin-add-to-cart form');
      this.idInput = this.form ? this.form.querySelector('[name="id"]') : null;
      this.priceTarget = document.querySelector('[data-variant-price]');
      this.atc = document.querySelector('[data-atc]');
      this.skuTarget = document.querySelector('[data-variant-sku]');
      this.gallery = document.querySelector('vin-gallery');

      try { this.variants = JSON.parse(this.querySelector('[type="application/json"]').textContent); }
      catch (err) { this.variants = []; }

      this.addEventListener('change', this.onChange.bind(this));
    }

    selectedOptions() {
      return $$('fieldset', this).map(function (fs) {
        var checked = fs.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    onChange() {
      var chosen = this.selectedOptions();

      // Reflect the choice in the group label immediately.
      $$('fieldset', this).forEach(function (fs) {
        var checked = fs.querySelector('input:checked');
        var out = fs.querySelector('[data-selected-value]');
        if (out && checked) out.textContent = checked.value;
      });

      var match = this.variants.filter(function (v) {
        return chosen.every(function (opt, i) { return opt === null || v.options[i] === opt; });
      })[0];

      if (!match) { this.setUnavailable(); return; }

      if (this.idInput) this.idInput.value = match.id;
      if (this.priceTarget && match.price_html) this.priceTarget.innerHTML = match.price_html;
      if (this.skuTarget) this.skuTarget.textContent = match.sku || '';

      if (this.atc) {
        var label = this.atc.querySelector('.btn__label');
        if (match.available) {
          this.atc.removeAttribute('disabled');
          if (label) label.textContent = t('addToCart', 'Add to cart');
        } else {
          this.atc.setAttribute('disabled', '');
          if (label) label.textContent = t('soldOut', 'Sold out');
        }
      }

      // Swap the lead product image to the chosen colour.
      if (this.gallery && match.featured_media_id) {
        this.gallery.featureMedia(match.featured_media_id);
      }

      var url = new URL(window.location.href);
      url.searchParams.set('variant', match.id);
      window.history.replaceState({}, '', url.toString());
    }

    setUnavailable() {
      if (!this.atc) return;
      this.atc.setAttribute('disabled', '');
      var label = this.atc.querySelector('.btn__label');
      if (label) label.textContent = t('unavailable', 'Unavailable');
    }
  }
  customElements.define('vin-variants', VinVariants);

  /* ---------- <vin-qty> — product page quantity ------------ */
  class VinQty extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.addEventListener('click', function (e) {
        var step = e.target.closest('[data-qty-step]');
        if (!step || !this.input) return;
        e.preventDefault();
        var min = parseInt(this.input.min, 10) || 1;
        var next = (parseInt(this.input.value, 10) || min) + parseInt(step.dataset.qtyStep, 10);
        this.input.value = Math.max(min, next);
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }.bind(this));
    }
  }
  customElements.define('vin-qty', VinQty);

  /* ==========================================================
     <vin-gallery> — mobile snap rail with dot readout
     ========================================================== */
  class VinGallery extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('.gallery');
      this.items = $$('.gallery__item', this);
      this.dots = $$('.gallery__dot', this);
      if (!this.rail || this.items.length < 2) return;

      this.dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () { this.goTo(i); }.bind(this));
      }, this);

      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.intersectionRatio > 0.6) {
            var index = this.items.indexOf(entry.target);
            this.setActive(index);
          }
        }, this);
      }.bind(this), { root: this.rail, threshold: [0.6] });
      this.items.forEach(function (item) { io.observe(item); });
    }

    setActive(index) {
      this.dots.forEach(function (dot, i) {
        var on = i === index;
        dot.classList.toggle('is-active', on);
        dot.setAttribute('aria-current', on ? 'true' : 'false');
      });
    }

    /* Move the chosen colour's media to the lead slot.

       The node is moved rather than floated with CSS `order`: `order` would
       leave DOM order and visual order disagreeing, so assistive technology
       would announce a different sequence than the one on screen. */
    featureMedia(mediaId) {
      if (!mediaId || !this.rail) return;

      var target = this.querySelector('.gallery__item[data-media-id="' + mediaId + '"]');
      if (!target || target === this.rail.firstElementChild) return;

      this.rail.prepend(target);
      this.items = $$('.gallery__item', this);   // indices moved with it

      if (!reduced.matches) {
        target.classList.remove('just-featured');
        void target.offsetWidth;                 // restart on a rapid re-pick
        target.classList.add('just-featured');
      }

      if (window.innerWidth < 1024) this.goTo(0);
      else this.setActive(0);
    }

    goTo(index) {
      if (!this.items || !this.items[index]) return;
      var item = this.items[index];
      if (window.innerWidth < 1024 && this.rail) {
        this.rail.scrollTo({
          left: item.offsetLeft - this.rail.offsetLeft,
          behavior: reduced.matches ? 'auto' : 'smooth'
        });
      } else {
        item.scrollIntoView({ behavior: reduced.matches ? 'auto' : 'smooth', block: 'nearest' });
      }
      this.setActive(index);
    }
  }
  customElements.define('vin-gallery', VinGallery);

  /* ==========================================================
     <vin-accordion> — native-ish disclosure, animated by grid
     ========================================================== */
  class VinAccordion extends HTMLElement {
    connectedCallback() {
      this.single = this.hasAttribute('single');
      this.triggers = $$('.accordion__trigger', this);

      this.triggers.forEach(function (trigger) {
        trigger.addEventListener('click', function () {
          var open = trigger.getAttribute('aria-expanded') === 'true';
          if (this.single && !open) {
            this.triggers.forEach(function (other) { this.setState(other, false); }, this);
          }
          this.setState(trigger, !open);
        }.bind(this));
      }, this);
    }

    /* The panel is resolved through aria-controls rather than an adjacent
       sibling selector: triggers are wrapped in a heading for correct document
       outline, which puts the panel out of the button's sibling scope. */
    panelFor(trigger) {
      var id = trigger.getAttribute('aria-controls');
      return id ? document.getElementById(id) : trigger.nextElementSibling;
    }

    setState(trigger, open) {
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      var panel = this.panelFor(trigger);
      if (panel) panel.classList.toggle('is-open', open);
    }
  }
  customElements.define('vin-accordion', VinAccordion);

  /* ==========================================================
     <vin-nav> — desktop dropdown, hover + keyboard
     ========================================================== */
  class VinNav extends HTMLElement {
    connectedCallback() {
      this.items = $$('.nav__item', this).filter(function (i) { return i.querySelector('.nav__panel'); });
      this.items.forEach(function (item) {
        var trigger = item.querySelector('.nav__link');
        var panel = item.querySelector('.nav__panel');
        if (!trigger || !panel) return;
        var timer;

        trigger.setAttribute('aria-expanded', 'false');

        function open() { clearTimeout(timer); item.classList.add('is-open'); trigger.setAttribute('aria-expanded', 'true'); }
        function close() { timer = setTimeout(function () { item.classList.remove('is-open'); trigger.setAttribute('aria-expanded', 'false'); }, 120); }

        item.addEventListener('mouseenter', open);
        item.addEventListener('mouseleave', close);
        item.addEventListener('focusin', open);
        item.addEventListener('focusout', function (e) {
          if (!item.contains(e.relatedTarget)) close();
        });
        trigger.addEventListener('click', function (e) {
          // Trigger is a real link; only intercept when it is a pure toggle.
          if (trigger.getAttribute('href') === '#') {
            e.preventDefault();
            item.classList.contains('is-open') ? close() : open();
          }
        });
        item.addEventListener('keydown', function (e) {
          if (e.key === 'Escape' && item.classList.contains('is-open')) {
            item.classList.remove('is-open');
            trigger.setAttribute('aria-expanded', 'false');
            trigger.focus();
          }
        });
      });
    }
  }
  customElements.define('vin-nav', VinNav);

  /* ==========================================================
     Scroll reveal — one observer for the whole page
     ========================================================== */
  function initReveal() {
    var targets = $$('[data-reveal]');
    if (!targets.length) return;

    if (reduced.matches || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        // Stagger siblings inside a group: 60ms apart, capped.
        var group = el.closest('[data-reveal-group]');
        if (group && !el.dataset.revealDelayApplied) {
          var peers = $$('[data-reveal]', group);
          var index = peers.indexOf(el);
          if (index > 0) el.style.setProperty('--reveal-delay', Math.min(index * 60, 320) + 'ms');
          el.dataset.revealDelayApplied = '1';
        }
        el.classList.add('is-revealed');
        io.unobserve(el);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

    targets.forEach(function (el) { io.observe(el); });
  }

  /* ==========================================================
     Hero drift — a very slow parallax, rAF-throttled
     ========================================================== */
  function initParallax() {
    var layers = $$('[data-parallax]');
    if (!layers.length || reduced.matches) return;
    if (!window.matchMedia('(min-width: 768px)').matches) return;

    var ticking = false;

    function update() {
      var vh = window.innerHeight;
      layers.forEach(function (el) {
        var host = el.closest('.hero__media') || el.parentElement;
        var rect = host.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > vh) return;
        var depth = parseFloat(el.dataset.parallax) || 0.12;
        var progress = (rect.top + rect.height / 2 - vh / 2) / vh;
        var shift = -(progress * depth * 100);
        el.style.transform = 'translate3d(0,' + shift.toFixed(2) + 'px,0) scale(1.08)';
      });
      ticking = false;
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /* ==========================================================
     Header stuck state
     ========================================================== */
  function initHeader() {
    var header = $('[data-header]');
    if (!header) return;
    var sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'position:absolute;top:0;height:1px;width:1px;';
    document.body.prepend(sentinel);

    if (!('IntersectionObserver' in window)) { header.classList.add('is-stuck'); return; }
    var io = new IntersectionObserver(function (entries) {
      header.classList.toggle('is-stuck', !entries[0].isIntersecting);
    }, { threshold: 0 });
    io.observe(sentinel);
  }

  /* Publish the header's real height so every sticky offset, the scroll
     padding that keeps focus visible, and the overlay pull-up all agree with
     what is actually on screen — including after a logo or font swap. */
  function syncHeaderHeight() {
    var header = $('[data-header]');
    if (!header) return;

    var last = 0;
    function apply() {
      var h = Math.round(header.getBoundingClientRect().height);
      if (!h || h === last) return;      // no-op guard keeps ResizeObserver calm
      last = h;
      document.documentElement.style.setProperty('--header-h', h + 'px');
    }

    apply();
    if ('ResizeObserver' in window) {
      new ResizeObserver(apply).observe(header);
    } else {
      window.addEventListener('resize', apply, { passive: true });
    }
    // Web fonts change the header's height after they swap in.
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(apply);
  }

  /* ==========================================================
     Sticky mobile buy bar — appears once the real CTA is gone
     ========================================================== */
  function initBuyBar() {
    var bar = $('[data-buy-bar]');
    var anchor = $('[data-buy-bar-anchor]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;

    document.body.classList.add('has-buy-bar');

    var io = new IntersectionObserver(function (entries) {
      var gone = !entries[0].isIntersecting && entries[0].boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', gone);
      bar.setAttribute('aria-hidden', gone ? 'false' : 'true');
      // Inert while hidden so it never traps tab order behind the viewport.
      $$('a, button, input', bar).forEach(function (el) {
        el.tabIndex = gone ? 0 : -1;
      });
    }, { threshold: 0 });
    io.observe(anchor);
  }

  /* ==========================================================
     Related products — Shopify's recommendations endpoint.
     Fetched after load so it never blocks the product page.
     ========================================================== */
  function initRecommendations() {
    var host = $('[data-recommendations]');
    if (!host || !host.dataset.url || !window.fetch) return;

    fetch(host.dataset.url)
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var fresh = parsed.querySelector('[data-recommendations]');
        if (!fresh) return;
        host.innerHTML = fresh.innerHTML;
        initReveal();
      })
      .catch(function () { /* leave the section empty rather than erroring */ });
  }

  /* ==========================================================
     Wide tables in merchant-authored page content
     Wrapped so they scroll themselves rather than pushing the
     page sideways — the merchant just pastes a plain <table>.
     ========================================================== */
  function initContentTables() {
    $$('.article-body table').forEach(function (table) {
      if (table.parentElement && table.parentElement.classList.contains('table-scroll')) return;
      var wrap = document.createElement('div');
      wrap.className = 'table-scroll';
      wrap.setAttribute('tabindex', '0');          // scrollable region must be reachable by keyboard
      wrap.setAttribute('role', 'region');
      var caption = table.querySelector('caption');
      wrap.setAttribute('aria-label', caption ? caption.textContent.trim() : 'Table');
      table.parentNode.insertBefore(wrap, table);
      wrap.appendChild(table);
    });
  }

  /* ==========================================================
     Form error summary — focus it after a failed submit
     ========================================================== */
  function initErrorSummary() {
    var summary = $('[data-error-summary]');
    if (summary && !summary.hidden) summary.focus();
  }

  /* ---------- boot ----------------------------------------- */
  function boot() {
    initHeader();
    syncHeaderHeight();
    initReveal();
    initParallax();
    initBuyBar();
    initRecommendations();
    initContentTables();
    initErrorSummary();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-run reveals on sections re-rendered by the theme editor.
  document.addEventListener('shopify:section:load', function () {
    initReveal();
    initParallax();
  });
})();
